import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { clientApi } from '@/lib/adminApi';

/**
 * Sesión F (ADD 3) — Client-portal decisions-unread badge.
 *
 * Polls `/api/client/evaluation-requests/decisions-unread-count`:
 *   - on mount,
 *   - every 60s,
 *   - whenever the user navigates,
 *   - and immediately marks-as-seen when they land on /client/requests.
 *
 * Returns the integer count to display in the sidebar badge. Returns 0
 * for any non-client role so the hook is safe to call from a shared
 * routes file.
 */
export function useDecisionsBadge() {
  const { user } = useApp();
  const location = useLocation();
  const [count, setCount] = useState(0);

  const isClient = user && (user.role === 'client_admin' || user.role === 'client_hr');

  const refetch = useCallback(async () => {
    if (!isClient) { setCount(0); return; }
    try {
      const data = await clientApi.unreadDecisionsCount();
      setCount(Number(data?.count || 0));
    } catch {
      // silent — badge is a nice-to-have
    }
  }, [isClient]);

  // Mark seen when the client opens the Requests page, then refetch.
  useEffect(() => {
    if (!isClient) return;
    if (location.pathname === '/client/requests') {
      clientApi.markDecisionsSeen()
        .then(() => setCount(0))
        .catch(() => {});
    }
  }, [isClient, location.pathname]);

  // Initial + periodic refresh.
  useEffect(() => {
    if (!isClient) return;
    refetch();
    const id = setInterval(refetch, 60_000);
    return () => clearInterval(id);
  }, [isClient, refetch]);

  // Refetch on navigation (in case advisor approved while user was on another page).
  useEffect(() => {
    if (!isClient) return;
    if (location.pathname !== '/client/requests') {
      refetch();
    }
  }, [location.pathname, isClient, refetch]);

  return count;
}

export default useDecisionsBadge;
